/**
 * The dashboard frame for one route tree: Shell + Sidebar + TopBar + FiltersBar
 * (+ a page's floating dock), all derived from the matched route's registry
 * entry. Pages render in the Outlet.
 */
import { PageState, Shell } from '@deepdive-ui';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router';

import { NAV_ITEMS, navSectionsFor, routeById, viewOf, type AppRoute } from '@/app/router/registry';
import { env } from '@/config/env';
import { useDataSource } from '@/features/data/DataSourceContext';
import { PageContext, type PageMeta } from '@/features/data/PageContext';
import {
  canOpen,
  isExecutive,
  isReleased,
  openNavItems,
  releasedNavItems,
  viewAccess,
} from '@/features/shell/access';
import { FiltersBarContainer } from '@/features/shell/FiltersBarContainer';
import { PageDock } from '@/features/shell/docks';
import { ShellProviders } from '@/features/shell/ShellProviders';
import { SidebarContainer } from '@/features/shell/SidebarContainer';
import { TopBarContainer } from '@/features/shell/TopBarContainer';
import { useProfilePhotoUrl } from '@/features/settings/profilePhoto';
import NotFoundPage from '@/pages/NotFound';
import { useAppDispatch } from '@/store/hooks';
import { openDialog } from '@/store/slices/uiSlice';

export type RouteHandle = { routeId: string };

/** An executive's sidebar: the executive view's items alone, so no Settings either. */
const EXECUTIVE_NAV_ITEMS: ReadonlySet<string> = new Set(
  NAV_ITEMS.filter((item) =>
    navSectionsFor('executive').some((section) => section.id === item.section),
  ).map((item) => item.id),
);

function useCurrentRoute(): AppRoute | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle as RouteHandle | undefined;
    if (handle?.routeId) return routeById.get(handle.routeId);
  }
  return undefined;
}

export function DashboardLayout({ visibleNavItems }: { visibleNavItems: ReadonlySet<string> }) {
  const matched = useCurrentRoute();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatch();
  const profilePhotoUrl = useProfilePhotoUrl();
  const { source, bootstrap } = useDataSource();
  const workspaceId = bootstrap.workspace.id;
  // A deployed live build holds client-only modules back; the dev server and /test open them all.
  const gated = source === 'live' && !env.isDev;
  // An executive (`EXECUTIVE_USERS`) sees the executive view and nothing else.
  const executive = source === 'live' && isExecutive(bootstrap.session.user.id);
  const access = viewAccess(matched && viewOf(matched), { executive, gated });
  // A module not released to this workspace is not there at all: its pages are not found.
  const unreleased =
    access === 'not-found' ||
    (gated && matched !== undefined && !isReleased(matched.navItem, workspaceId));
  const route = unreleased ? undefined : matched;
  const navItems = useMemo(() => {
    const released = gated ? releasedNavItems(visibleNavItems, workspaceId) : visibleNavItems;
    const shown = executive
      ? new Set([...released].filter((id) => EXECUTIVE_NAV_ITEMS.has(id)))
      : released;
    return openNavItems(shown, bootstrap.modules);
  }, [gated, executive, visibleNavItems, workspaceId, bootstrap.modules]);
  // A module outside the person's access says so, instead of a page of failed cards.
  const allowed = canOpen(bootstrap.modules, route?.navItem);

  // The page scrolls inside <main>, not the window — reset it on navigation.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  const page = useMemo<PageMeta>(
    () => ({ routeId: route?.id ?? '', filtersType: route?.filters ?? null }),
    [route],
  );

  if (access === 'to-executive') return <Navigate to="/executive" replace />;

  return (
    <PageContext.Provider value={page}>
      <ShellProviders>
        <Shell
          mainRef={mainRef}
          label={route ? undefined : 'Not found'}
          sidebar={
            <SidebarContainer
              view={viewOf(route)}
              activeItemId={route?.navItem}
              visibleItemIds={navItems}
              profilePhotoUrl={profilePhotoUrl}
              onInviteMember={executive ? undefined : () => dispatch(openDialog('invite-member'))}
            />
          }
          header={route && <TopBarContainer key={route.id} route={route} />}
          filters={
            allowed && route?.filters ? <FiltersBarContainer type={route.filters} /> : undefined
          }
          dock={route?.dock ? <PageDock kind={route.dock} /> : undefined}
        >
          {unreleased ? (
            <NotFoundPage />
          ) : allowed ? (
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          ) : (
            <PageState
              title="You don’t have access to this module"
              description="Ask a workspace admin to give you access to it."
            />
          )}
        </Shell>
      </ShellProviders>
    </PageContext.Provider>
  );
}
